import { DeviceType } from '$lib/constants';
import logger from '$lib/logger';
import { modbusSettings } from '$lib/PersistedState.svelte';
import { modbusDebugEnabled } from '$lib/State.svelte';
import { invoke } from '@tauri-apps/api/core';
import { BaseDevice } from '../base';

class ModbusClientError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ModbusClientError';
  }
}

interface CoffeeTechReading {
  bean: number;
  drum: number;
  burner: number;
  exhaust: number;
  // drumSpeed: number;
  // fanSpeed: number;
  // setpoint: number;
}

export class CoffeeTechDevice extends BaseDevice {
  type: DeviceType = DeviceType.COFFEE_TECH;

  private drumTemp: number | null = null;
  private exhaustTemp: number | null = null;
  private drumSpeed: number | null = null;
  // private fanSpeed: number | null = null;
  // private setpoint: number | null = null;
  private burnerPower: number | null = null;

  private static readonly REGISTERS = {
    BEAN_TEMP: 100,
    DRUM_TEMP: 80,
    BURNER_POWER: 37,
    EXHAUST_TEMP: 85,
    DRUM_SPEED: 47,
    FAN_SPEED: 46,
    SETPOINT: 48
  };

  private static readonly SLAVE_ID = 0;

  protected async onConnect(): Promise<boolean> {
    return true;
  }

  protected onDisconnect(): void {
    // No specific cleanup needed
  }

  protected async updateReadings(): Promise<void> {
    try {
      const readings = await this.readAllValues(modbusSettings.current.host);

      this.drumTemp = readings.drum;
      this.exhaustTemp = readings.exhaust;
      // this.drumSpeed = readings.drumSpeed;
      // this.fanSpeed = readings.fanSpeed;
      // this.setpoint = readings.setpoint;
      this.burnerPower = readings.burner;

      this.localReadings = [
        readings.bean,
        this.tcSettings?.[1].active ? readings.drum : null,
        this.tcSettings?.[2].active ? readings.exhaust : null,
        null
      ];

      if (modbusDebugEnabled.current === true) {
        logger.debug('DEVICE_COFFEETECH_READINGS', { bean: readings.bean });
      }
    } catch (error) {
      if (error instanceof ModbusClientError) {
        logger.error(`Modbus error: ${error.message}`);
      } else {
        logger.error(`Unknown error: ${error}`);
      }
      throw error;
    }
  }

  protected handleError(error: unknown): void {
    logger.warn(`CoffeeTech polling error: ${error}`);
  }

  protected async getPowerLevel(): Promise<number | null> {
    return this.burnerPower;
  }

  public getDrumTemperature(): number | null {
    return this.drumTemp;
  }

  public getExhaustTemperature(): number | null {
    return this.exhaustTemp;
  }

  public getDrumSpeed(): number | null {
    return this.drumSpeed;
  }

  // public getFanSpeed(): number | null {
  //   return this.fanSpeed;
  // }

  // public getSetpoint(): number | null {
  //   return this.setpoint;
  // }

  private async readAllValues(host: string): Promise<CoffeeTechReading> {
    try {
      // const group1: number[] = await invoke('read_modbus', {
      //   host,
      //   reg: CoffeeTechDevice.REGISTERS.FAN_SPEED,
      //   count: 3,
      //   slave: CoffeeTechDevice.SLAVE_ID
      // });

      const [beanReading, drumReading, burnerReading, exhaustReading] = await Promise.all([
        this.readSingleRegister(host, CoffeeTechDevice.REGISTERS.BEAN_TEMP),
        this.readSingleRegister(host, CoffeeTechDevice.REGISTERS.DRUM_TEMP),
        this.readSingleRegister(host, CoffeeTechDevice.REGISTERS.BURNER_POWER),
        this.readSingleRegister(host, CoffeeTechDevice.REGISTERS.EXHAUST_TEMP)
      ]);

      return {
        bean: CoffeeTechDevice.convertTemperature(beanReading),
        drum: CoffeeTechDevice.convertTemperature(drumReading),
        burner: CoffeeTechDevice.convertPower(burnerReading),
        exhaust: CoffeeTechDevice.convertTemperature(exhaustReading)
        // fanSpeed: CoffeeTechDevice.convertFrequency(group1[0]),
        // drumSpeed: CoffeeTechDevice.convertFrequency(group1[1]),
        // setpoint: CoffeeTechDevice.convertTemperature(group1[2])
      };
    } catch (err: any) {
      const regex = /error creating modbus client/i;
      if (regex.test(err)) {
        throw new ModbusClientError('Could not create Modbus client');
      } else {
        logger.warn(`Modbus reading error: ${err}`);
      }

      return {
        bean: 0,
        drum: 0,
        burner: 0,
        exhaust: 0
        // drumSpeed: 0,
        // fanSpeed: 0,
        // setpoint: 0
      };
    }
  }

  private async readSingleRegister(host: string, address: number): Promise<number> {
    const results: number[] = await invoke('read_modbus', {
      host,
      reg: address,
      count: 1,
      slave: CoffeeTechDevice.SLAVE_ID
    });
    return results[0];
  }

  static convertTemperature(rawValue: number): number {
    return rawValue / 10.0;
  }

  static convertPower(rawValue: number): number {
    return rawValue / 10.0;
  }

  static convertFrequency(rawValue: number): number {
    return rawValue / 10.0;
  }
}
